import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  SegmentedControl,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { unwrap } from '../../api/client';
import { InviteCard } from '../../components/InviteCard';
import { getSubscription, openPortal, startCheckout } from '../../api/gen';
import { euro, shortDate } from '../../lib/text';
import { useOrg } from '../../lib/session';

// Abo & Rechnung.
//
// One number does the work: a seat is a child. The page shows what the next
// invoice will say rather than what the last one did, because that is the
// question a parent who just added a second child is actually asking.
//
// It also says, plainly, what a plan gates — starting new sessions — and what
// it does not, which is everything already there. A family that stops paying
// keeps the Lernverlauf.
export function BillingPage() {
  const org = useOrg();
  const [interval, setInterval] = useState('month');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['subscription'],
    queryFn: () => unwrap(getSubscription({ headers: org })),
  });

  const checkout = useMutation({
    mutationFn: () =>
      unwrap(
        startCheckout({
          headers: org,
          body: { interval: interval as 'month' | 'year', returnUrl: window.location.href },
        }),
      ),
    onSuccess: (link) => link && window.location.assign(link.url),
    onError: (err) => notifications.show({ color: 'red', message: (err as Error).message }),
  });

  const portal = useMutation({
    mutationFn: () => unwrap(openPortal({ headers: org, body: { returnUrl: window.location.href } })),
    onSuccess: (link) => link && window.location.assign(link.url),
    onError: (err) => notifications.show({ color: 'red', message: (err as Error).message }),
  });

  if (isLoading) return <Loader />;
  if (!data) return <Alert color="red">Der Abo-Status ist gerade nicht lesbar.</Alert>;

  if (!data.configured) {
    return (
      <Stack>
        <Title order={3}>Abo</Title>
        <Alert variant="light">
          Auf dieser Installation ist keine Zahlung eingerichtet. Es wird nichts abgerechnet und
          nichts gesperrt.
        </Alert>
      </Stack>
    );
  }

  const perChild = interval === 'year' ? data.pricePerChildYearCents : data.pricePerChildMonthCents;
  const total = perChild * Math.max(1, data.seats);

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={3}>Abo</Title>
        <Badge
          size="lg"
          variant="light"
          color={data.plan === 'paid' ? 'green' : data.plan === 'trial' ? 'blue' : 'gray'}
        >
          {data.plan === 'paid' ? 'Aktiv' : data.plan === 'trial' ? 'Testphase' : 'Kostenlos'}
        </Badge>
      </Group>

      {data.plan === 'trial' && data.trialEndsAt ? (
        <Alert variant="light" color="blue">
          Die Testphase läuft noch bis {shortDate(data.trialEndsAt)}. Danach bleibt alles
          Bisherige lesbar — nur neue Sitzungen brauchen dann ein Abo.
        </Alert>
      ) : null}

      {data.plan === 'free' ? (
        <Alert>
          Neue Sitzungen sind gerade gesperrt. Der gesamte Lernverlauf und alle Berichte bleiben
          sichtbar.
        </Alert>
      ) : null}

      <Card withBorder padding="lg">
        <Stack>
          <Group justify="space-between">
            <div>
              <Text fw={600}>
                {data.seats} Kind{data.seats === 1 ? '' : 'er'}
              </Text>
              <Text size="xs" c="dimmed">
                Ein Kind ist ein Platz. Kommt eines dazu, wächst die Rechnung mit.
              </Text>
            </div>
            <SegmentedControl
              value={data.plan === 'paid' ? (data.interval || 'month') : interval}
              onChange={setInterval}
              disabled={data.plan === 'paid'}
              data={[
                { value: 'month', label: 'monatlich' },
                { value: 'year', label: 'jährlich' },
              ]}
            />
          </Group>

          <Group gap="xs" align="baseline">
            <Text fw={700} size="xl">
              {euro(total)}
            </Text>
            <Text size="sm" c="dimmed">
              pro {interval === 'year' ? 'Jahr' : 'Monat'} · {euro(perChild)} je Kind
            </Text>
          </Group>
          {interval === 'year' ? (
            <Text size="xs" c="dimmed">
              Jährlich sind zwei Monate frei.
            </Text>
          ) : null}

          {data.plan === 'paid' ? (
            <Stack gap="xs">
              {data.hasPayment && data.cardLast4 ? (
                <Text size="sm" c="dimmed">
                  {data.cardBrand} •••• {data.cardLast4}
                  {data.status && data.status !== 'active' ? ` · ${data.status}` : ''}
                </Text>
              ) : null}
              <Group>
                <Button variant="light" loading={portal.isPending} onClick={() => portal.mutate()}>
                  Zahlung und Rechnungen verwalten
                </Button>
                <Button variant="subtle" onClick={() => void refetch()}>
                  Aktualisieren
                </Button>
              </Group>
            </Stack>
          ) : (
            <Button loading={checkout.isPending} onClick={() => checkout.mutate()}>
              Abo abschließen
            </Button>
          )}
        </Stack>
      </Card>

      <InviteCard />
    </Stack>
  );
}
